import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRolesOverview } from '@/features/roles/hooks/useRoles';
import * as repo from '../data/usersRepository';
import { useUsersMutation } from '../hooks/useUsers';
import type { UserOverview, UserRoleAssignment } from '../types';
import { emptyScope, isScopeComplete, scopeText, type ScopeValue } from '../utils/scope';
import { ScopeFields } from './ScopeFields';

interface UserRolesDialogProps {
  /** The row is read from the list so the dialog refreshes after every change */
  user: UserOverview | null;
  onOpenChange: (open: boolean) => void;
}

/** Roles of one user (assign / revoke). The database enforces what the caller may hand out. */
export function UserRolesDialog({ user, onOpenChange }: UserRolesDialogProps) {
  const { data: roles = [] } = useRolesOverview();
  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<ScopeValue>(emptyScope);
  const [revoking, setRevoking] = useState<UserRoleAssignment | null>(null);

  const assign = useUsersMutation(repo.assignUserRole, { success: 'Rol asignado' });
  const revoke = useUsersMutation(repo.revokeUserRole, { success: 'Rol quitado' });
  const activeRoles = roles.filter((r) => r.is_active);
  const canAssign = !!user && !!roleId && isScopeComplete(scope) && !assign.isPending;

  const submit = () => {
    if (!user || !canAssign) return;
    assign.mutate(
      {
        userId: user.id, roleId, scope: scope.scope,
        districtId: scope.scope === 'DISTRICT' ? scope.districtId : undefined,
        regionId: scope.scope === 'REGION' ? scope.regionId : undefined,
      },
      { onSuccess: () => { setRoleId(''); setScope(emptyScope); } },
    );
  };

  return (
    <>
      <Dialog open={!!user} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Roles de {user?.full_name}</DialogTitle>
            <DialogDescription>Un usuario puede tener varios roles, cada uno con su alcance.</DialogDescription>
          </DialogHeader>

          <section aria-label="Roles actuales" className="space-y-2">
            {user?.roles.length === 0 && <p className="text-sm text-muted-foreground">Sin roles: no puede hacer nada en el sistema.</p>}
            {user?.roles.map((a) => (
              <div key={a.assignment_id} className="flex items-center justify-between gap-2 rounded-md border p-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">{a.role_name}</p>
                  <Badge variant="secondary">{scopeText(a)}</Badge>
                </div>
                <Button variant="ghost" size="icon" aria-label={`Quitar ${a.role_name}`} onClick={() => setRevoking(a)}>
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            ))}
          </section>

          <section aria-label="Asignar un rol" className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-semibold">Asignar un rol</h3>
            <div className="space-y-1.5">
              <Label htmlFor="assign-role">Rol</Label>
              <Select value={roleId} onValueChange={setRoleId}>
                <SelectTrigger id="assign-role"><SelectValue placeholder="Selecciona un rol" /></SelectTrigger>
                <SelectContent>{activeRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ScopeFields value={scope} onChange={setScope} idPrefix="assign" />
            <Button onClick={submit} disabled={!canAssign}>
              {assign.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Plus className="mr-2 size-4" />}
              Asignar rol
            </Button>
          </section>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revoking} onOpenChange={(open) => !open && setRevoking(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el rol «{revoking?.role_name}»?</AlertDialogTitle>
            <AlertDialogDescription>
              {user?.full_name} perderá lo que este rol le permite ({revoking ? scopeText(revoking) : ''}).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => revoking && revoke.mutate(revoking.assignment_id, { onSuccess: () => setRevoking(null) })}
            >
              Quitar rol
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
